import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BRAND, NAV_LINKS } from "../../data";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 inset-x-0 z-50"
    >
      <div
        className={`transition-all duration-500 ${
          scrolled
            ? "bg-navy/95 backdrop-blur-md shadow-[0_10px_40px_-15px_rgba(0,0,0,0.5)]"
            : "bg-navy"
        }`}
      >
        <nav className="mx-auto max-w-[1400px] px-5 md:px-10 h-[68px] md:h-[76px] flex items-center justify-between">
          <a
            href="#home"
            data-testid="nav-logo"
            className="flex items-center gap-3 group"
          >
            <img
              src={BRAND.logo}
              alt="SocialImpactCLE logo — a starburst of diverse hands"
              data-testid="nav-logo-img"
              className="h-11 w-11 md:h-12 md:w-12 rounded-xl bg-white object-contain p-1 ring-2 ring-white/10 transition-transform duration-500 group-hover:scale-105"
            />
            <span className="font-display font-extrabold tracking-tight text-white text-lg md:text-xl">
              SocialImpact<span className="text-brand-orange">CLE</span>
            </span>
          </a>

          <div className="hidden lg:flex items-center gap-9">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="relative text-sm font-medium text-white/75 hover:text-white transition-colors duration-300 py-1 group"
              >
                {l.label}
                <span className="absolute -bottom-0.5 left-0 h-[2px] w-0 bg-brand-orange transition-all duration-300 group-hover:w-full" />
              </a>
            ))}
            <a
              href="#programs"
              data-testid="nav-cta"
              className="inline-flex items-center rounded-full bg-brand-orange px-6 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-white hover:text-navy hover:-translate-y-0.5"
            >
              Explore Programs
            </a>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-white p-2 -mr-2"
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </nav>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-navy border-t border-white/10"
          >
            <div className="px-5 py-5 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="py-3 text-lg font-medium text-white/85 border-b border-white/5"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#programs"
                onClick={() => setOpen(false)}
                className="mt-4 inline-flex justify-center rounded-full bg-brand-orange px-6 py-3 text-base font-semibold text-white"
              >
                Explore Programs
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
